'use client';
import { useEffect, useState, useCallback, useRef } from 'react';
import { getCable } from '../cable';
import type { EventMetadata } from '../types';

type EventHandler = (data: any, metadata?: EventMetadata) => void;

interface UseEventsSubscriptionOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  eventHandlers?: Record<string, EventHandler>;
}

export function useCableSubscription(
  userId: string | string[],
  options: UseEventsSubscriptionOptions = {},
) {
  const [isConnected, setIsConnected] = useState(false);
  const eventHandlersRef = useRef<Record<string, EventHandler>>({});
  const pendingEventsRef = useRef<Record<string, { payload: any; metadata?: EventMetadata }[]>>({});
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initialize event handlers from options
  useEffect(() => {
    if (options.eventHandlers) {
      eventHandlersRef.current = { ...options.eventHandlers };
    }
  }, []);

  const subscriptionIds = Array.from(
    new Set(typeof userId === 'string' ? [userId] : userId),
  ).filter(Boolean);
  const subscriptionKey = subscriptionIds.join('\u0000');
  const subscriptionIdsRef = useRef(subscriptionIds);
  subscriptionIdsRef.current = subscriptionIds;

  useEffect(() => {
    const cable = getCable();
    const ids = subscriptionIdsRef.current;
    if (!cable || ids.length === 0) return;

    const connectedSubscriptionIds = new Set<string>();
    const subscriptions = ids.map((id) => {
      const subscription = cable.subscribeTo('EventsChannel', { user_id: id });

      subscription.on('connect', () => {
        if (connectedSubscriptionIds.has(id)) return;
        connectedSubscriptionIds.add(id);
        setIsConnected(connectedSubscriptionIds.size > 0);
        optionsRef.current.onConnect?.();
      });

      subscription.on('disconnect', () => {
        if (!connectedSubscriptionIds.delete(id)) return;
        setIsConnected(connectedSubscriptionIds.size > 0);
        optionsRef.current.onDisconnect?.();
      });

      subscription.on('message', (data: any) => {
        try {
          // Support both server formats: { message: "{...}" } and direct object { type, payload }
          const raw = typeof data?.message !== 'undefined' ? data.message : data;
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (parsed) {
            const inferredType =
              parsed.type ||
              parsed.event ||
              (parsed.thread_id && parsed.conversation_id ? 'real_time_message' : undefined);
            const eventType = inferredType;
            const payload = parsed.payload || parsed;
            const metadata: EventMetadata = { channelUserId: id };

            if (!eventType) {
              console.warn('Ignoring socket event without an event type');
              return;
            }

            const handler = eventHandlersRef.current[eventType];
            if (handler) {
              handler(payload, metadata);
            } else {
              // Buffer the event until a handler is registered
              pendingEventsRef.current[eventType] = pendingEventsRef.current[eventType] || [];
              pendingEventsRef.current[eventType].push({ payload, metadata });
              if (pendingEventsRef.current[eventType].length > 50) {
                pendingEventsRef.current[eventType].shift();
              }
              const consol = console;
              consol.warn(`No handler registered for event type: ${eventType}`);
            }
          }
        } catch (error) {
          console.error('Error processing message:', error);
        }
      });

      return subscription;
    });

    return () => {
      subscriptions.forEach((subscription) => subscription.disconnect());
      if (mountedRef.current) setIsConnected(false);
    };
  }, [subscriptionKey]);

  const addEventHandler = useCallback((eventType: string, handler: EventHandler) => {
    eventHandlersRef.current = {
      ...eventHandlersRef.current,
      [eventType]: handler,
    };
    // Flush any buffered events for this type
    const buffered = pendingEventsRef.current[eventType];
    if (buffered && buffered.length > 0) {
      buffered.forEach(({ payload, metadata }) => {
        try {
          handler(payload, metadata);
        } catch (e) {
          console.error('Error handling buffered event', e);
        }
      });
      pendingEventsRef.current[eventType] = [];
    }
  }, []);

  const removeEventHandler = useCallback((eventType: string) => {
    const newHandlers = { ...eventHandlersRef.current };
    delete newHandlers[eventType];
    eventHandlersRef.current = newHandlers;
  }, []);

  return {
    isConnected,
    addEventHandler,
    removeEventHandler,
  };
}
